import { AppIcon, AppIcons } from '@backrs_ui/common-ui';

import { Box, ButtonBase, SxProps, Theme } from '@mui/material';

type ViewOption<T extends string> = Readonly<{ value: T; label: string; icon: AppIcons }>;

type ViewToggleProps<T extends string> = Readonly<{
  id: string;
  options: readonly ViewOption<T>[];
  value: T;
  onChange: (value: T) => void;
}>;

export const ViewToggle = <T extends string>({
  id,
  options,
  value,
  onChange,
}: ViewToggleProps<T>) => (
  <Box id={id} role="group" sx={groupSx}>
    {options.map((option) => (
      <ButtonBase
        key={option.value}
        id={`${id}-${option.value}`}
        aria-label={option.label}
        aria-pressed={option.value === value}
        onClick={() => onChange(option.value)}
        sx={itemSx(option.value === value)}
      >
        <AppIcon
          iconName={option.icon}
          diameter="20px"
          color={
            option.value === value
              ? 'Colors/Foreground/fg-secondary'
              : 'Colors/Foreground/fg-quaternary'
          }
        />
      </ButtonBase>
    ))}
  </Box>
);

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const itemSx =
  (current: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '44px',
    paddingBlock: backrs.spacing['spacing-md'],
    paddingInline: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${current ? backrs.colors['Colors/Border/border-primary'] : 'transparent'}`,
    backgroundColor: current ? backrs.colors['Colors/Background/bg-primary'] : 'transparent',
    boxShadow: current ? backrs.shadows['shadow-xs'] : 'none',
  });
